import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Clock, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Static blog images
import aiAnsweringImg from "@/assets/blog/ai-answering-service.jpg";
import aiVsHumanImg from "@/assets/blog/ai-vs-human-cost.jpg";
import aiReceptionistImg from "@/assets/blog/ai-receptionist-small-business.jpg";
import reduceCostsImg from "@/assets/blog/reduce-support-costs.jpg";
import aiRealEstateImg from "@/assets/blog/ai-real-estate.jpg";
import aiPhoneSupportImg from "@/assets/blog/ai-phone-support.jpg";
import aiMedicalImg from "@/assets/blog/ai-medical-clinic.jpg";
import aiWhatsappImg from "@/assets/blog/ai-whatsapp-integration.jpg";
import aiRestaurantsImg from "@/assets/blog/ai-restaurants.jpg";
import bestAiSoftwareImg from "@/assets/blog/best-ai-software.jpg";
import aiCallCenterImg from "@/assets/blog/ai-call-center-setup.jpg";
import voiceVsChatbotImg from "@/assets/blog/voice-vs-chatbot.jpg";
import futureAiImg from "@/assets/blog/future-ai-service.jpg";
import chatgptMcpImg from "@/assets/blog/chatgpt-mcp-phone-call.jpg";
import aiVoiceCloningImg from "@/assets/blog/ai-voice-cloning-phone-agent.jpg";
import openclawPhoneImg from "@/assets/blog/openclaw-phone-calls.jpg";

interface BlogCard {
  slug: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  date: string;
  image: string;
}

const staticPosts: BlogCard[] = [
  { slug: "openclaw-phone-calls", title: "How to Add Phone Calls to OpenClaw Easily with Call24x7", description: "Give your OpenClaw AI agent the power of real phone calls using Call24x7.AI.", category: "Integration", readTime: "8 min read", date: "February 28, 2026", image: openclawPhoneImg },
  { slug: "ai-voice-cloning-phone-agent", title: "AI Phone Agent with Your Voice: Clone Your Voice in 30 Seconds", description: "Create an AI phone agent that sounds exactly like you.", category: "Voice Cloning", readTime: "7 min read", date: "December 21, 2025", image: aiVoiceCloningImg },
  { slug: "chatgpt-mcp-phone-calls", title: "Make Phone Calls from ChatGPT with Call24x7.AI MCP Integration", description: "Revolutionary: Make real phone calls directly from ChatGPT.", category: "Innovation", readTime: "8 min read", date: "December 15, 2025", image: chatgptMcpImg },
  { slug: "what-is-ai-answering-service", title: "What Is an AI Answering Service and How Does It Work?", description: "Discover how AI answering services revolutionize customer communication.", category: "AI Basics", readTime: "5 min read", date: "December 9, 2025", image: aiAnsweringImg },
  { slug: "ai-vs-human-agents-cost-comparison", title: "AI vs Human Agents: Cost Comparison for Small Businesses", description: "A detailed breakdown of costs between AI and human agents.", category: "Cost Analysis", readTime: "7 min read", date: "November 12, 2025", image: aiVsHumanImg },
  { slug: "ai-receptionist-for-small-business", title: "AI Receptionist for Small Business: Complete Guide 2025", description: "Learn how AI receptionists help small businesses handle calls 24/7.", category: "Small Business", readTime: "6 min read", date: "November 5, 2025", image: aiReceptionistImg },
  { slug: "reduce-customer-support-costs-with-ai", title: "How AI Can Reduce Customer Support Costs by 70%", description: "Strategies to dramatically cut support costs with AI automation.", category: "ROI & Savings", readTime: "6 min read", date: "October 15, 2025", image: reduceCostsImg },
  { slug: "ai-for-real-estate-agents", title: "AI Phone Agents for Real Estate: Capture Every Lead", description: "How real estate agents use AI phone agents to qualify leads 24/7.", category: "Real Estate", readTime: "7 min read", date: "October 2, 2025", image: aiRealEstateImg },
  { slug: "top-ways-businesses-use-ai-phone-support", title: "Top 5 Ways Businesses Use AI for Phone Support", description: "Most popular use cases for AI phone agents.", category: "Use Cases", readTime: "5 min read", date: "September 8, 2025", image: aiPhoneSupportImg },
  { slug: "ai-for-medical-clinics", title: "AI Phone Agents for Medical Clinics & Healthcare", description: "HIPAA-compliant AI phone agents for medical clinics.", category: "Healthcare", readTime: "8 min read", date: "August 28, 2025", image: aiMedicalImg },
  { slug: "integrate-ai-customer-support-whatsapp", title: "How to Integrate AI Customer Support With WhatsApp & Webchat", description: "Extend your AI support across multiple channels.", category: "Integration", readTime: "8 min read", date: "August 1, 2025", image: aiWhatsappImg },
  { slug: "ai-for-restaurants", title: "AI Phone Agents for Restaurants: Handle Reservations 24/7", description: "How restaurants use AI to handle reservations and orders.", category: "Restaurants", readTime: "6 min read", date: "July 25, 2025", image: aiRestaurantsImg },
  { slug: "best-ai-phone-agent-software", title: "Best AI Phone Agent Software in 2025", description: "Compare the top AI phone agent software solutions.", category: "Software Review", readTime: "10 min read", date: "June 20, 2025", image: bestAiSoftwareImg },
  { slug: "how-to-set-up-ai-call-center", title: "How to Set Up an AI Call Center in 2025", description: "Complete guide to setting up an AI call center.", category: "Implementation", readTime: "12 min read", date: "May 15, 2025", image: aiCallCenterImg },
  { slug: "ai-voice-agent-vs-chatbot", title: "AI Voice Agent vs Chatbot: Which Is Better?", description: "Detailed comparison of capabilities, costs, and preferences.", category: "Comparison", readTime: "7 min read", date: "April 10, 2025", image: voiceVsChatbotImg },
  { slug: "future-of-ai-customer-service", title: "The Future of AI Customer Service: 2025-2030", description: "Expert predictions for AI customer service trends.", category: "Industry Trends", readTime: "9 min read", date: "March 5, 2025", image: futureAiImg },
];

const FeaturedBlogPosts = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const currentLang = i18n.language || "en";

  const { data: dynamicPosts } = useQuery({
    queryKey: ["featured-dynamic-blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("slug, category, read_time, date, author, image_url, translations")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const dynamicCards: BlogCard[] = (dynamicPosts || []).map((p: any) => {
    const trans = p.translations as Record<string, { title: string; description: string }>;
    const loc = trans[currentLang] || trans["en"] || { title: p.slug, description: "" };
    return {
      slug: p.slug,
      title: loc.title,
      description: loc.description,
      category: p.category,
      readTime: p.read_time,
      date: p.date,
      image: p.image_url || "/placeholder.svg",
    };
  });

  // Merge and deduplicate by slug (dynamic takes priority for fresher content)
  const allPosts = [...dynamicCards, ...staticPosts];
  const seen = new Set<string>();
  const uniquePosts = allPosts.filter(p => {
    if (seen.has(p.slug)) return false;
    seen.add(p.slug);
    return true;
  });
  // Sort by date descending to always show the 6 most recent
  const sorted = uniquePosts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const top6 = sorted.slice(0, 6);

  return (
    <section className="py-20 px-2 sm:px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary text-primary-foreground font-semibold border-primary">
            {t("featuredBlog.badge")}
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t("featuredBlog.title")}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t("featuredBlog.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {top6.map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`}>
              <Card className="h-full hover:shadow-lg hover:border-primary/50 transition-all group overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    width="550"
                    height="309"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <CardHeader className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-xs">
                      {post.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <CardTitle className="text-lg group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="text-sm line-clamp-2">
                    {post.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </span>
                    {isRTL ? <ArrowLeft className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" /> : <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/blog">
            <Button variant="outline" size="lg" className="group">
              {t("featuredBlog.viewAll")}
              {isRTL ? <ArrowLeft className={`w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform`} /> : <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBlogPosts;
